/**
 * Sjablonen: zwart wordt uitgesneden, wit blijft folie. Folie die nergens met
 * de rand verbonden is (het binnenstuk van een O, A, 0 of 8 zonder brug) valt
 * er bij het snijden uit. Deze module telt die eilandjes op een grijsraster.
 */

/** Canonieke slugs van producten waar het ontwerp als sjabloon wordt uitgesneden. */
export const STENCIL_PRODUCT_SLUGS = new Set(['sjabloon-op-maat-met-eigen-tekst-of-logo']);

/** Langste zijde waarop de server het raster meet: dun genoeg voor snelheid, dik genoeg voor bruggen. */
export const STENCIL_RASTER_MAX_SIDE = 1200;

export function isStencilProduct(productSlug: string | null | undefined) {
  return STENCIL_PRODUCT_SLUGS.has(String(productSlug || ''));
}

/** RGBA naar grijs, transparant telt als wit (folie). */
export function rgbaToGray(data: ArrayLike<number>, width: number, height: number): Uint8Array {
  const gray = new Uint8Array(width * height);
  for (let i = 0; i < gray.length; i++) {
    const r = data[i * 4], g = data[i * 4 + 1], b = data[i * 4 + 2], a = data[i * 4 + 3] / 255;
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    gray[i] = Math.round(255 - a * (255 - luminance));
  }
  return gray;
}

/**
 * Aantal losse foliestukjes. Wit dat via wit de buitenrand raakt hoort bij het
 * sjabloon; elk ander wit gebied vanaf `minArea` pixels is een eilandje.
 * Kleinere witte spikkels zijn JPEG-ruis of anti-aliasing en tellen niet.
 */
export function countStencilIslands(
  gray: ArrayLike<number>,
  width: number,
  height: number,
  options: { threshold?: number; minArea?: number } = {},
): number {
  const total = width * height;
  if (!width || !height || gray.length < total) return 0;
  const threshold = options.threshold ?? 128;
  const minArea = options.minArea ?? Math.max(16, Math.round(total / 20_000));
  const seen = new Uint8Array(total);
  const stack = new Int32Array(total);

  const fill = (start: number) => {
    let top = 0;
    let area = 0;
    stack[top++] = start;
    seen[start] = 1;
    while (top) {
      const index = stack[--top];
      area++;
      const x = index % width;
      const neighbours = [
        x > 0 ? index - 1 : -1,
        x < width - 1 ? index + 1 : -1,
        index >= width ? index - width : -1,
        index + width < total ? index + width : -1,
      ];
      for (const next of neighbours) {
        if (next >= 0 && !seen[next] && gray[next] >= threshold) {
          seen[next] = 1;
          stack[top++] = next;
        }
      }
    }
    return area;
  };

  for (let x = 0; x < width; x++) {
    for (const index of [x, total - width + x]) if (!seen[index] && gray[index] >= threshold) fill(index);
  }
  for (let y = 0; y < height; y++) {
    for (const index of [y * width, y * width + width - 1]) if (!seen[index] && gray[index] >= threshold) fill(index);
  }

  let islands = 0;
  for (let index = 0; index < total; index++) {
    if (!seen[index] && gray[index] >= threshold && fill(index) >= minArea) islands++;
  }
  return islands;
}
